import type { PortfolioReport } from '@interfaces/portfolioReport';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Badge } from '@ovComponents/2-component/badge/badge';
import { Chip } from '@ovComponents/2-component/chip/chip';
import { Pagination } from '@ovComponents/2-component/pagination/pagination';
import { StatusTag } from '@ovComponents/2-component/statusTag/statusTag';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { Box, Skeleton, Typography, useTheme } from 'hephaestus/web';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';
import type { OptimizerAuditV2Record } from '../../types/audit.types';
import type {
  RunReportImpactFilter,
  RunReportPortfolioFilters,
} from '../../types/runTable.types';
import { activateOnEnterOrSpace } from '../../utils/presentation/activateOnEnterOrSpace';
import { resolveRebalanceModeForDisplay } from '../../utils/presentation/rebalanceModePresentation';
import type { RunAttentionBadge } from '../../utils/runTable/buildRunAttentionPortfolioRows';
import {
  prepareRunReportTableRows,
  type RunReportTableSort,
  resolveRunReportTableSortDirection,
} from '../../utils/runTable/runReportTableRows';
import { bulkRebalanceTableHeadDividerSx } from '../auditGrid/tableStyles';
import type { BulkRebalanceReportDetailSelection } from '../reportModal/reportDetailModal';
import { BulkRebalancePortfolioTableCell } from '../shared/portfolioSubtitleRow';
import { BulkRebalanceRebalanceModeBadge } from '../shared/rebalanceModeBadge';
import { BulkRebalanceTradeActivityLine } from '../shared/tradeActivityLine';
import { BulkRebalanceRunReportTableHeader } from './runReportImpactFilters';

export type BulkRebalanceRunReportsTableProps = {
  reports: PortfolioReport[];
  audits: OptimizerAuditV2Record[];
  tickerMap: Record<string, string>;
  loading: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
  reportsFullyLoaded?: boolean;
  portfolioFilters: RunReportPortfolioFilters;
  onPortfolioFiltersChange: (value: RunReportPortfolioFilters) => void;
  impactFilter: RunReportImpactFilter;
  onImpactFilterChange: (value: RunReportImpactFilter) => void;
  emptyMessage?: string;
  onPageChange: (page: number) => void;
  onOpenReportDetail?: (selection: BulkRebalanceReportDetailSelection) => void;
  reportsResolving?: boolean;
};

const TABLE_COLUMN_COUNT = 7;

const getImpactBadgeLabel = (
  badge: RunAttentionBadge,
  t: (key: string) => string,
) => {
  switch (badge) {
    case 'failed':
      return t('runInsights.reviewNext.badgeFailed');
    case 'tax':
      return t('runInsights.reviewNext.badgeTax');
    case 'highTurnover':
      return t('runInsights.reviewNext.badgeHighTurnover');
    case 'liquidate':
      return t('runInsights.reviewNext.badgeLiquidate');
    case 'transfer':
      return t('runInsights.reviewNext.badgeTransfer');
    default:
      return badge;
  }
};

const getImpactBadgeColor = (badge: RunAttentionBadge) => {
  switch (badge) {
    case 'failed':
      return 'negative' as const;
    case 'tax':
      return 'warning' as const;
    case 'highTurnover':
      return 'supportTwo' as const;
    case 'liquidate':
      return 'supportOne' as const;
    case 'transfer':
      return 'supportTwo' as const;
    default:
      return 'supportOne' as const;
  }
};

export const BulkRebalanceRunReportsTable = ({
  reports,
  audits,
  tickerMap,
  loading,
  page,
  pageSize,
  totalCount,
  reportsFullyLoaded = true,
  portfolioFilters,
  onPortfolioFiltersChange,
  impactFilter,
  onImpactFilterChange,
  emptyMessage,
  onPageChange,
  onOpenReportDetail,
  reportsResolving = false,
}: BulkRebalanceRunReportsTableProps) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['bulkRebalance']);
  const theme = useTheme();
  const [sort, setSort] = useState<RunReportTableSort>('impact');
  const noReportsMessage = emptyMessage ?? t('detail.noReports');
  const showLoadingSkeleton =
    loading && reports.length === 0 && audits.length === 0;
  const hasTableData = reports.length > 0 || audits.length > 0;
  const reportsById = useMemo(
    () => new Map(reports.map((report) => [report.id, report])),
    [reports],
  );
  const filterScopeRows = useMemo(
    () =>
      prepareRunReportTableRows(translateBackendString, reports, audits, {
        impactFilter,
        sort: 'impact',
      }).rows,
    [audits, impactFilter, reports],
  );
  const { rows, matchingCount, globalMatchingCount } = useMemo(
    () =>
      prepareRunReportTableRows(translateBackendString, reports, audits, {
        impactFilter,
        portfolioFilters,
        sort,
      }),
    [audits, impactFilter, portfolioFilters, reports, sort],
  );

  const pageCount = Math.max(1, Math.ceil(matchingCount / pageSize));

  const resetToFirstPage = () => {
    if (page !== 1) {
      onPageChange(1);
    }
  };

  const handleImpactFilterChange = (value: RunReportImpactFilter) => {
    onImpactFilterChange(value);
    resetToFirstPage();
  };

  const handlePortfolioFiltersChange = (value: RunReportPortfolioFilters) => {
    onPortfolioFiltersChange(value);
    resetToFirstPage();
  };

  const toggleMarketValueSort = () => {
    setSort((current) => {
      if (current === 'marketValueDesc') {
        return 'marketValueAsc';
      }

      if (current === 'marketValueAsc') {
        return 'marketValueDesc';
      }

      return 'marketValueDesc';
    });
    resetToFirstPage();
  };

  const toggleTradesSort = () => {
    setSort((current) => {
      if (current === 'tradesDesc') {
        return 'tradesAsc';
      }

      if (current === 'tradesAsc') {
        return 'tradesDesc';
      }

      return 'tradesDesc';
    });
    resetToFirstPage();
  };

  const marketValueSortDirection = resolveRunReportTableSortDirection(
    sort,
    'marketValueAsc',
    'marketValueDesc',
  );

  const tradesSortDirection = resolveRunReportTableSortDirection(
    sort,
    'tradesAsc',
    'tradesDesc',
  );

  const visibleRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [page, pageSize, rows]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <BulkRebalanceRunReportTableHeader
        filterScopeRows={filterScopeRows}
        reportsById={reportsById}
        audits={audits}
        tickerMap={tickerMap}
        impactFilter={impactFilter}
        portfolioFilters={portfolioFilters}
        onImpactFilterChange={handleImpactFilterChange}
        onPortfolioFiltersChange={handlePortfolioFiltersChange}
        matchingCount={matchingCount}
        globalMatchingCount={globalMatchingCount}
        reportsFullyLoaded={reportsFullyLoaded}
        totalCount={totalCount}
        loadedReportCount={filterScopeRows.length}
      />

      <Table
        sx={{
          minWidth: 960,
          ...bulkRebalanceTableHeadDividerSx(theme),
        }}
        data-testid='bulk-rebalance-run-reports-table'
      >
        <TableHead>
          <TableRow>
            <TableHeadCell>{t('reports.portfolio')}</TableHeadCell>
            <TableHeadCell>{t('reports.accounts')}</TableHeadCell>
            <TableHeadCell>{t('reports.model')}</TableHeadCell>
            <TableHeadCell
              right
              isSortable
              sortDirection={marketValueSortDirection}
              onClick={toggleMarketValueSort}
              data-testid='bulk-rebalance-report-sort-market-value'
            >
              {t('reports.marketValue')}
            </TableHeadCell>
            <TableHeadCell>{t('reports.status')}</TableHeadCell>
            <TableHeadCell>{t('runInsights.reviewNext.flags')}</TableHeadCell>
            <TableHeadCell
              right
              isSortable
              sortDirection={tradesSortDirection}
              onClick={toggleTradesSort}
              data-testid='bulk-rebalance-report-sort-trades'
            >
              {t('reports.trades')}
            </TableHeadCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {showLoadingSkeleton &&
            Array.from({ length: 3 }).map((_, index) => (
              <TableRow key={`report-skeleton-${index}`}>
                {Array.from({ length: TABLE_COLUMN_COUNT }).map(
                  (__, cellIndex) => (
                    <TableCell key={`report-skeleton-cell-${cellIndex}`}>
                      <Skeleton variant='text' width='80%' />
                    </TableCell>
                  ),
                )}
              </TableRow>
            ))}

          {!showLoadingSkeleton && !hasTableData && (
            <TableRow>
              <TableCell colSpan={TABLE_COLUMN_COUNT}>
                <Typography variant='bodyMedium' color='textSecondary'>
                  {noReportsMessage}
                </Typography>
              </TableCell>
            </TableRow>
          )}

          {!showLoadingSkeleton && hasTableData && rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={TABLE_COLUMN_COUNT}>
                <Typography variant='bodyMedium' color='textSecondary'>
                  {t('reports.noFilterMatches')}
                </Typography>
              </TableCell>
            </TableRow>
          )}

          {!showLoadingSkeleton &&
            visibleRows.map((row) => {
              const canOpenDetail = Boolean(onOpenReportDetail && row.audit);
              // 'failed' is already surfaced by the Status column.
              const flagBadges = row.impactBadges.filter(
                (badge) => badge !== 'failed',
              );
              const showModeBadge = Boolean(
                resolveRebalanceModeForDisplay(row.audit),
              );
              const hasFlags = flagBadges.length > 0 || showModeBadge;

              const openDetail = () => {
                if (!canOpenDetail || !row.audit) {
                  return;
                }

                onOpenReportDetail?.({
                  reportId: row.reportId,
                  report: row.report,
                  audit: row.audit,
                  clientLabel: row.unresolvedReport
                    ? t('reports.unresolvedPortfolio')
                    : row.clientLabel,
                  goalLabel: row.unresolvedReport
                    ? t('reports.unresolvedPortfolioHint')
                    : row.goalLabel,
                });
              };
              const portfolioName = row.unresolvedReport
                ? t('reports.unresolvedPortfolio')
                : [row.clientLabel, row.goalLabel].filter(Boolean).join(' · ');

              return (
                <TableRow
                  key={row.reportId}
                  pointer={canOpenDetail}
                  role={canOpenDetail ? 'button' : undefined}
                  tabIndex={canOpenDetail ? 0 : undefined}
                  aria-label={
                    canOpenDetail
                      ? t('reports.openDetailAria', { name: portfolioName })
                      : undefined
                  }
                  data-testid={`bulk-rebalance-report-row-${row.reportId}`}
                  onClick={openDetail}
                  onKeyDown={
                    canOpenDetail
                      ? activateOnEnterOrSpace(openDetail)
                      : undefined
                  }
                >
                  <TableCell>
                    {row.unresolvedReport && reportsResolving ? (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Skeleton variant='text' width='70%' />
                        <Skeleton variant='text' width='45%' />
                      </Box>
                    ) : (
                      <BulkRebalancePortfolioTableCell
                        clientLabel={
                          row.unresolvedReport
                            ? t('reports.unresolvedPortfolio')
                            : row.clientLabel
                        }
                        goalLabel={
                          row.unresolvedReport ? undefined : row.goalLabel
                        }
                        report={row.unresolvedReport ? undefined : row.report}
                        flagsTestId={`bulk-rebalance-report-currency-flags-${row.reportId}`}
                        secondaryHint={
                          row.unresolvedReport ? (
                            <Typography
                              variant='labelSmall'
                              color='textSecondary'
                            >
                              {t('reports.unresolvedPortfolioHint')}
                            </Typography>
                          ) : undefined
                        }
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {row.accountTypes.length > 0 ? (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {row.accountTypes.map((accountType) => (
                          <Chip key={accountType} text={accountType} small />
                        ))}
                      </Box>
                    ) : (
                      <Typography
                        variant='labelSmall'
                        color={theme.color.outlineVariant}
                      >
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.unresolvedReport && reportsResolving ? (
                      <Skeleton variant='text' width='80%' />
                    ) : (
                      <Typography variant='bodySmall'>
                        {row.modelLabel}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell right>
                    <Typography variant='bodySmall'>
                      {formatMoneyValue(row.marketValueCents)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row.passed === true && (
                      <StatusTag
                        icon={CheckCircleOutlinedIcon}
                        label={t('reports.statusPassed')}
                        color='positive'
                        size='small'
                      />
                    )}
                    {row.passed === false && (
                      <StatusTag
                        icon={HighlightOffIcon}
                        label={t('reports.statusFailed')}
                        color='negative'
                        size='small'
                      />
                    )}
                    {row.passed === undefined && (
                      <Typography
                        variant='labelSmall'
                        color={theme.color.outlineVariant}
                      >
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {hasFlags ? (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {showModeBadge ? (
                          <BulkRebalanceRebalanceModeBadge audit={row.audit} />
                        ) : null}
                        {flagBadges.map((badge) => (
                          <Badge
                            key={`${row.reportId}-${badge}`}
                            label={getImpactBadgeLabel(badge, t)}
                            color={getImpactBadgeColor(badge)}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography
                        variant='labelSmall'
                        color={theme.color.outlineVariant}
                      >
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell right>
                    {row.tradeCount > 0 ? (
                      <BulkRebalanceTradeActivityLine
                        summary={{
                          tradeCount: row.tradeCount,
                          totalBuyCents: row.totalBuyCents,
                          totalSellCents: row.totalSellCents,
                        }}
                      />
                    ) : (
                      <Typography
                        variant='bodySmall'
                        color={theme.color.outlineVariant}
                      >
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>

      {!loading && matchingCount > pageSize && reportsFullyLoaded && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Pagination
            page={page}
            count={pageCount}
            onChange={(_, nextPage) => onPageChange(nextPage)}
          />
        </Box>
      )}
    </Box>
  );
};
